import { useCallback, useMemo } from "react";

import { useFeaturesByLayer } from "fond/hooks/useFeaturesByLayer";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";

export interface LayerCopy {
  sourceKey: string;
  destKey: string;
  displayName?: string;
}

/**
 * Copies features from each source layer into the corresponding destination layer via the import pipeline.
 */
export const useCopyLayerFeatures = (versionId: string, copies: LayerCopy[], defaultDisplayName: string) => {
  const dispatch = useAppDispatch();

  const sourceKeys = useMemo(() => copies.map((c) => c.sourceKey), [copies]);
  const { featuresByLayer, isLoading } = useFeaturesByLayer(versionId, sourceKeys);

  const hasSourceFeatures = copies.some((c) => (featuresByLayer[c.sourceKey]?.length ?? 0) > 0);

  const importStates = useAppSelector((state) => state.imports[versionId]);
  const isCopying = copies.some((c) => isImportInProgress(importStates?.[c.destKey]?.status));

  const copyFeatures = useCallback(async () => {
    await Promise.all(
      copies.map(async ({ sourceKey, destKey, displayName }) => {
        const features = featuresByLayer[sourceKey] ?? [];
        if (features.length === 0) return;
        const file = new File([JSON.stringify(removeDrawingProperties(featureCollection(features)))], "layer.geojson", {
          type: "application/geo+json",
        });
        await dispatch(addImport({ versionId, layerKey: destKey, displayName: displayName ?? defaultDisplayName, files: [file] }));
      })
    );
  }, [copies, defaultDisplayName, dispatch, featuresByLayer, versionId]);

  return { hasSourceFeatures, isCopying, isLoading, copyFeatures };
};
